import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  CircularProgress,
  Alert,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  Add as AddIcon,
  Refresh as RefreshIcon,
  PlayArrow as StartIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Assessment as AnalysisIcon
} from '@mui/icons-material';
import { DataGrid } from '@mui/x-data-grid';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import * as api from '../utils/api';
import { format } from 'date-fns';

const CampaignAnalysis = () => {
  const navigate = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [taskToDelete, setTaskToDelete] = useState(null);

  const fetchTasks = useCallback(async (isRefresh = false) => {
    try {
      if (isRefresh) setRefreshing(true); else setLoading(true);
      setError(null);
      const response = await api.getAnalysisTasks();
      setTasks(response.data.data || []);
    } catch (err) {
      console.error('Error fetching analysis tasks:', err);
      setError('Failed to load analysis tasks');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { fetchTasks(); }, [fetchTasks]);

  const handleCreateTask = () => navigate('/campaign-analysis/create');
  const handleRefresh = () => fetchTasks(true);
  const handleStartTask = async (taskId) => {
    try { await api.startAnalysisTask(taskId); fetchTasks(true); } catch (e) { console.error(e); }
  };
  const handleDeleteTask = (task) => { setTaskToDelete(task); setDeleteDialogOpen(true); };
  const confirmDeleteTask = async () => {
    if (!taskToDelete) return;
    try {
      await api.deleteAnalysisTask(taskToDelete.id);
      setTasks(tasks.filter(t => t.id !== taskToDelete.id));
      setDeleteDialogOpen(false);
      setTaskToDelete(null);
    } catch (e) { console.error(e); }
  };
  const handleViewResults = (taskId) => navigate(`/campaign-analysis/${taskId}/results`);
  const handleViewProgress = (taskId) => navigate(`/campaign-analysis/${taskId}/progress`);

  const getStatusColor = (status) => ({
    'draft': 'default',
    'collecting_dialogs': 'info',
    'ready_for_processing': 'info',
    'processing': 'warning',
    'completed': 'success',
    'failed': 'error',
    'paused': 'warning'
  })[status] || 'default';

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed': return '✅';
      case 'processing': return '⏳';
      case 'failed': return '❌';
      default: return '📋';
    }
  };

  const columns = [
    { field: 'name', headerName: 'Task name', flex: 2, minWidth: 200 },
    { field: 'campaign_name', headerName: 'Campaign', flex: 1.5, minWidth: 150, valueGetter: (params) => params.row.Campaign?.name || 'N/A' },
    { field: 'status', headerName: 'Status', width: 150, renderCell: (params) => (
      <Chip label={`${getStatusIcon(params.value)} ${params.value}`} color={getStatusColor(params.value)} size="small" variant="outlined" />
    )},
    { field: 'progress', headerName: 'Progress', width: 120, renderCell: (params) => {
      const { total_dialogs, processed_dialogs } = params.row; if (!total_dialogs) return 'N/A'; return `${processed_dialogs || 0}/${total_dialogs}`;
    }},
    { field: 'stats', headerName: 'Stats', width: 150, valueGetter: (params) => {
      const r = params.row; const a = r.stats_real_dialogs; const n = r.stats_noreply; const t = r.stats_total_chats; return (a||a===0) && (n||n===0) && (t||t===0) ? `${a}/${n}/${t}` : '-';
    }},
    { field: 'created_at', headerName: 'Created', width: 150, valueFormatter: (params) => params.value ? format(new Date(params.value), 'dd.MM.yyyy HH:mm') : 'N/A' },
    { field: 'completed_at', headerName: 'Completed', width: 150, valueFormatter: (params) => params.value ? format(new Date(params.value), 'dd.MM.yyyy HH:mm') : 'N/A' },
    { field: 'actions', headerName: 'Actions', width: 200, sortable: false, renderCell: (params) => {
      const task = params.row;
      return (
        <Box>
          <Tooltip title="View progress"><IconButton size="small" onClick={() => handleViewProgress(task.id)} color="info"><ViewIcon /></IconButton></Tooltip>
          {task.status === 'draft' && (
            <Tooltip title="Start task"><IconButton size="small" onClick={() => handleStartTask(task.id)} color="primary"><StartIcon /></IconButton></Tooltip>
          )}
          {task.status === 'completed' && (
            <Tooltip title="View results"><IconButton size="small" onClick={() => handleViewResults(task.id)} color="success"><AnalysisIcon /></IconButton></Tooltip>
          )}
          {task.status === 'draft' && (
            <Tooltip title="Delete task"><IconButton size="small" onClick={() => handleDeleteTask(task)} color="error"><DeleteIcon /></IconButton></Tooltip>
          )}
        </Box>
      );
    }}
  ];

  if (loading) {
    return (
      <DashboardLayout>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
          <CircularProgress size={60} />
        </Box>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" gutterBottom>Campaign analysis</Typography>
          <Typography variant="body1" color="text.secondary">Create and manage tasks to analyze completed campaigns using AI</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" startIcon={refreshing ? <CircularProgress size={16} /> : <RefreshIcon />} onClick={handleRefresh} disabled={refreshing}>
            {refreshing ? 'Refreshing...' : 'Refresh'}
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleCreateTask}>Create task</Button>
        </Box>
      </Box>

      {error && (<Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>{error}</Alert>)}

      <Paper sx={{ height: 600, width: '100%' }}>
        <DataGrid
          rows={tasks}
          columns={columns}
          pageSize={10}
          rowsPerPageOptions={[10, 25, 50]}
          disableSelectionOnClick
          loading={refreshing}
          getRowId={(row) => row.id}
          sx={{ border: 0, '& .MuiDataGrid-cell': { borderBottom: '1px solid #f0f0f0' } }}
        />
      </Paper>

      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Confirm deletion</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the task "{taskToDelete?.name}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={confirmDeleteTask} color="error" variant="contained">Delete</Button>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
};

export default CampaignAnalysis;
